import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  AccurateDateIntervalWithShift,
  Activity,
  ActivityWithCategoryAndLocationsAndReportedDetails,
  LocationAndDateTimeRange,
  Shift,
  ShiftState,
  WithPreparedAttributes,
} from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import { CreateNewViaDragAndDropService } from '@wilson/shift-timeline/services';
import {
  TimelineAssignedShiftValidationsStateService,
  TimelineCreateNewShiftV2,
  TimelineRemoveActivityFromShiftV2,
  TimelineRemoveShiftFromUserV2,
  TimelineUpdateShiftV2,
  TimelineUpsertActivityV2,
} from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { add, differenceInMinutes } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';
import { AddTransferModalComponent } from './add-transfer-modal.component';
import { TransferType } from './types';
import {
  createTransferShift,
  createTransferShiftForNextItem,
  createTransferShiftForPreviousItem,
} from './shift-transfer-helper.fn';
import { AccountService } from '@wilson/account';

const MOCK_ACTIVITY_ID = CreateNewViaDragAndDropService.MOCK_ACTIVITY_ID;

@Injectable()
export class ShiftTransferHandlerV2Service {
  private readonly accountService = inject(AccountService);
  private readonly locationsStoreService = inject(LocationsStoreService);
  private readonly store = inject(Store);
  private readonly nzModalService = inject(NzModalService);
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly shiftsService = inject(ShiftsService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineAssignedShiftValidationsStateService = inject(
    TimelineAssignedShiftValidationsStateService,
  );

  openTransferModal({
    shiftViews,
    userId,
    timelineCards,
    organizationalUnitId,
    index,
  }: {
    shiftViews: AccurateDateIntervalWithShift[];
    timelineCards: (LocationAndDateTimeRange & { shiftId?: string })[];
    userId: string;
    organizationalUnitId: string;
    index: number;
  }): void {
    const nextItem = timelineCards[index];
    const previousItem = timelineCards[index - 1];
    const isNextItemShift = !!nextItem?.shiftId;
    const isPreviousItemShift = !!previousItem?.shiftId;

    const previousShift = shiftViews.find(
      (shiftView) => shiftView.shift.id === previousItem.shiftId,
    )?.shift;
    const nextShift = shiftViews.find(
      (shiftView) => shiftView.shift.id === nextItem.shiftId,
    )?.shift;

    const notEditableShiftStates = [
      ShiftState.Submitted,
      ShiftState.AcceptedTimes,
      ShiftState.SubmittedToPayrollProvider,
    ];
    const isPreviousItemEditable = previousShift
      ? !notEditableShiftStates.includes(previousShift.state)
      : false;
    const isNextItemEditable = nextShift
      ? !notEditableShiftStates.includes(nextShift.state)
      : false;

    const minutesDifference = differenceInMinutes(
      new Date(nextItem.startDateTime),
      new Date(previousItem.endDateTime),
    );
    const newShiftStartDateTime = add(new Date(previousItem.endDateTime), {
      minutes: minutesDifference / 2,
    });

    const modalRef: NzModalRef<AddTransferModalComponent> =
      this.nzModalService.create<AddTransferModalComponent>({
        nzTitle: this.translate.instant('page.shift_timeline.add_transfer'),
        nzContent: AddTransferModalComponent,
        nzClosable: true,
        nzCentered: true,
        nzFooter: [
          {
            label: this.translate.instant('general.cancel'),
            type: 'default',
            onClick: (): void => {
              modalRef.destroy();
            },
          },
          {
            label: this.translate.instant('page.shift_timeline.add_transfer'),
            type: 'primary',
            disabled: (modalInstance): boolean => {
              if (
                modalInstance?.validateDurationSignal() &&
                modalInstance.selectedActivityType &&
                modalInstance.selectedDateTime
              ) {
                return false;
              } else {
                return true;
              }
            },
            onClick: async (
              modalInstance: AddTransferModalComponent,
            ): Promise<void> => {
              modalRef.destroy();
              const selectedTransferType = modalInstance.selectedTransferType;
              const selectedDuration = modalInstance.validateDurationSignal();
              const selectedActivityCategoryId =
                modalInstance.selectedActivityType;
              const selectedDateTime = modalInstance.selectedDateTime;

              switch (selectedTransferType) {
                case TransferType.Previous: {
                  await this.addTransferToPreviousShift({
                    shiftViews,
                    previousItem,
                    nextItem,
                    isNextItemShift,
                    duration: selectedDuration,
                    selectedActivityCategoryId,
                    userId,
                  });
                  break;
                }
                case TransferType.Next: {
                  await this.addTransferToNextShift({
                    shiftViews,
                    previousItem,
                    nextItem,
                    isPreviousItemShift,
                    duration: selectedDuration,
                    selectedActivityCategoryId,
                    userId,
                  });
                  break;
                }
                case TransferType.New: {
                  await this.addNewTransferShift({
                    shiftViews,
                    previousItem,
                    nextItem,
                    duration: selectedDuration,
                    selectedActivityCategoryId,
                    dateTime: selectedDateTime,
                    userId,
                    organizationalUnitId,
                  });
                  break;
                }
              }
            },
          },
        ],
      });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.selectedDateTime = newShiftStartDateTime;
      modalRef.componentInstance.isNextShiftAvailable = isNextItemShift;
      modalRef.componentInstance.isPreviousShiftAvailable = isPreviousItemShift;
      modalRef.componentInstance.isPreviousShiftEditable =
        isPreviousItemEditable;
      modalRef.componentInstance.isNextShiftEditable = isNextItemEditable;
      modalRef.componentInstance.previousShift = previousItem;
      modalRef.componentInstance.nextShift = nextItem;
    }
  }

  private async addTransferToPreviousShift({
    shiftViews,
    duration,
    previousItem,
    selectedActivityCategoryId,
    userId,
    nextItem,
    isNextItemShift,
  }: {
    previousItem: LocationAndDateTimeRange & {
      shiftId?: string;
    };
    shiftViews: AccurateDateIntervalWithShift[];
    nextItem: LocationAndDateTimeRange & {
      shiftId?: string;
    };
    isNextItemShift: boolean;
    duration: number;
    selectedActivityCategoryId: string;
    userId: string;
  }): Promise<void> {
    const previousShift = shiftViews.find(
      (shiftView) => shiftView.shift.id === previousItem.shiftId,
    )?.shift;

    const shiftToUpdate = previousShift?.id
      ? await this.getShiftWithActivities(previousShift.id)
      : null;

    const homeLocationId = await this.getUserHomeLocationId(userId);
    const output = createTransferShiftForPreviousItem({
      previousShift,
      shiftViews,
      duration,
      selectedActivityCategoryId,
      isNextItemShift,
      nextItem,
      homeLocationId,
      previousShiftWithActivitiesToUpdate: shiftToUpdate,
    });
    if (output) {
      await this.handleShiftUpdate(
        output.previousShiftWithActivitiesToUpdate,
        output.oldShift,
        output.shiftWithoutActivities,
        userId,
        output.newActivity as ActivityWithCategoryAndLocationsAndReportedDetails,
      );
    } else {
      console.error('Failed to create transfer shift', output);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  private async addTransferToNextShift({
    nextItem,
    shiftViews,
    duration,
    selectedActivityCategoryId,
    userId,
    isPreviousItemShift,
    previousItem,
  }: {
    nextItem: LocationAndDateTimeRange & {
      shiftId?: string;
    };
    previousItem: LocationAndDateTimeRange & {
      shiftId?: string;
    };
    shiftViews: AccurateDateIntervalWithShift[];
    isPreviousItemShift: boolean;
    duration: number;
    selectedActivityCategoryId: string;
    userId: string;
  }): Promise<void> {
    const nextShift = shiftViews.find(
      (shiftView) => shiftView.shift.id === nextItem.shiftId,
    )?.shift;
    const nextShiftWithActivitiesToUpdate = nextShift?.id
      ? await this.getShiftWithActivities(nextShift.id)
      : null;

    const homeLocationId = await this.getUserHomeLocationId(userId);
    const output = createTransferShiftForNextItem({
      nextShift,
      shiftViews,
      duration,
      selectedActivityCategoryId,
      isPreviousItemShift,
      previousItem,
      nextShiftWithActivitiesToUpdate,
      homeLocationId,
    });
    if (output) {
      await this.handleShiftUpdate(
        output.nextShiftWithActivitiesToUpdate,
        output.oldShift,
        output.shiftWithoutActivities,
        userId,
        output.newActivity as ActivityWithCategoryAndLocationsAndReportedDetails,
      );
    } else {
      console.error('Failed to create transfer shift', output);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  private async getUserHomeLocationId(userId: string): Promise<string | null> {
    const userDetails = await firstValueFrom(
      this.accountService.getUser(userId, {
        relations: ['homeLocation', 'organizationalUnit'],
      }),
    );

    return (
      userDetails.homeLocationId ||
      userDetails.organizationalUnit?.locationId ||
      null
    );
  }

  private async addNewTransferShift({
    shiftViews,
    previousItem,
    nextItem,
    duration,
    selectedActivityCategoryId,
    dateTime,
    userId,
    organizationalUnitId,
  }: {
    shiftViews: AccurateDateIntervalWithShift[];
    previousItem: LocationAndDateTimeRange & {
      shiftId?: string;
    };
    nextItem: LocationAndDateTimeRange & {
      shiftId?: string;
    };
    duration: number;
    selectedActivityCategoryId: string;
    dateTime: Date;
    userId: string;
    organizationalUnitId: string;
  }): Promise<void> {
    const homeLocationId = await this.getUserHomeLocationId(userId);
    const output = await createTransferShift({
      shiftViews,
      previousItem,
      nextItem,
      duration,
      selectedActivityCategoryId,
      dateTime,
      userId,
      organizationalUnitId,
      homeLocationId,
      locationsStoreService: this.locationsStoreService,
      translate: this.translate,
    });

    if (output) {
      await this.handleShiftCreate(
        output.newShift,
        output.shiftWithoutActivities,
        userId,
        output.newShift
          .activities[0] as ActivityWithCategoryAndLocationsAndReportedDetails,
      );
    } else {
      console.error('Failed to create transfer shift', output);
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    }
  }

  private async handleShiftCreate(
    shiftToCreate: Shift,
    shiftWithoutActivities: Shift &
      WithPreparedAttributes & {
        id: string;
      },
    userId: string,
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
  ): Promise<void> {
    try {
      this.store.dispatch(
        new TimelineCreateNewShiftV2(
          { ...shiftWithoutActivities, isHighlighted: true },
          userId,
        ),
      );
      this.store.dispatch(
        new TimelineUpsertActivityV2(
          [{ ...activity, id: MOCK_ACTIVITY_ID }],
          shiftWithoutActivities.id,
        ),
      );
      this.createNewViaDragAndDropService.addUpdatingId(
        shiftWithoutActivities.id,
      );
      const shifts = await this.shiftsService.addMany(shiftToCreate);
      if (shifts[0].id) {
        this.store.dispatch([
          new TimelineRemoveActivityFromShiftV2(
            MOCK_ACTIVITY_ID,
            shiftWithoutActivities.id,
          ),
          new TimelineRemoveShiftFromUserV2({
            userId: userId,
            shiftId: shiftWithoutActivities.id,
          }),
          new TimelineCreateNewShiftV2(
            {
              ...shiftWithoutActivities,
              id: shifts[0].id,
              isHighlighted: true,
            },
            userId,
          ),
        ]);
        this.timelineCommonOperationsService.updateAssignedShiftsKPIs();
        this.timelineAssignedShiftValidationsStateService.queueValidationRequest(
          [shifts[0].id],
          {
            forceRefresh: true,
          },
        );

        await this.updateActivityOnTimeline(shifts[0].id);
      }

      this.createNewViaDragAndDropService.removeUpdatingId(
        shiftWithoutActivities.id,
      );
    } catch {
      this.store.dispatch([
        new TimelineRemoveActivityFromShiftV2(
          MOCK_ACTIVITY_ID,
          shiftWithoutActivities.id,
        ),
        new TimelineRemoveShiftFromUserV2({
          userId: userId,
          shiftId: shiftWithoutActivities.id,
        }),
      ]);
      this.createNewViaDragAndDropService.removeUpdatingId(
        shiftWithoutActivities.id,
      );
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } finally {
      this.timelineCommonOperationsService.updateUsersAssignmentsState([
        userId,
      ]);
    }
  }

  private async handleShiftUpdate(
    shiftToUpdate: Shift,
    oldShift: Shift &
      WithPreparedAttributes & {
        id: string;
      },
    shiftWithoutActivities: Shift &
      WithPreparedAttributes & {
        id: string;
      },
    userId: string,
    activity: ActivityWithCategoryAndLocationsAndReportedDetails,
  ): Promise<void> {
    try {
      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...shiftWithoutActivities,
          isHighlighted: true,
        }),
      );
      this.store.dispatch(
        new TimelineUpsertActivityV2(
          [{ ...activity, id: MOCK_ACTIVITY_ID }],
          shiftToUpdate.id as string,
        ),
      );
      await this.shiftsService.update(shiftToUpdate);
      await this.updateActivityOnTimeline(shiftToUpdate.id as string);
    } catch {
      this.store.dispatch(
        new TimelineUpdateShiftV2({
          ...oldShift,
          isHighlighted: true,
        }),
      );
      this.store.dispatch(
        new TimelineRemoveActivityFromShiftV2(
          MOCK_ACTIVITY_ID,
          shiftToUpdate.id as string,
        ),
      );
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
    } finally {
      this.timelineCommonOperationsService.updateUsersAssignmentsState([
        userId,
      ]);
    }
  }

  private async updateActivityOnTimeline(shiftId: string): Promise<void> {
    const shift = await this.getShiftWithActivities(shiftId);
    if (shift) {
      this.store.dispatch([
        new TimelineRemoveActivityFromShiftV2(MOCK_ACTIVITY_ID, shiftId),
        new TimelineUpsertActivityV2(
          shift.activities as ActivityWithCategoryAndLocationsAndReportedDetails[],
          shiftId,
        ),
      ]);
    }
  }

  private async getShiftWithActivities(shiftId: string): Promise<
    | (Shift & {
        id: string;
        activities: Activity[];
        priority: number | null;
        projectId: string | null;
      })
    | null
  > {
    try {
      return (await firstValueFrom(
        this.shiftsService.get(shiftId, {
          relations: [
            `organizationalUnit`,
            `user`,
            `shiftSeries`,
            `shiftCategory`,
            `activities`,
            `activities.endLocation`,
            `activities.startLocation`,
            `activities.activityCategory`,
            `activities.activityReports`,
            `activities.activityReports.location`,
            'labels',
          ],
        }),
      )) as Shift & {
        id: string;
        activities: Activity[];
        priority: number | null;
        projectId: string | null;
      };
    } catch {
      this.nzMessageService.error(
        this.translate.instant('general.something_went_wrong'),
      );
      return null;
    }
  }
}
